/**
 * StoreReceiptContainer
 * --
 */
import React from 'react';
import StoreReceiptPresenter from './StoreReceiptPresenter';
import {
  useGetShopInformations,
  useUpdateShopInformation,
  shopInformationQueryKeys,
} from 'hooks/ShopInformationHooks';
import { useQueryClient } from '@tanstack/react-query';
import MessageAlert from 'utils';

const INITIAL_VALUE = {
  receipt_information: '',
};

const StoreReceiptContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { defaultShopId, permission } = props;
  const queryClient = useQueryClient();

  /* ===== Data ===== */
  const query = {
    type: 'STORE',
    ...(defaultShopId && { shop_information_id: defaultShopId }),
  };
  const { data: Stores } = useGetShopInformations(query, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      return data;
    },
    initialData: [],
  });

  /* ===== Mutate ===== */
  // 매장 수정
  const { updateShopInformation } = useUpdateShopInformation();

  /* ===== STATE ===== */
  // 선택한 매장
  const [currentShop, setCurrentShop] = React.useState(null);
  // 입력 값
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  // 이전 값
  const [defaultValue, setDefaultValue] = React.useState(INITIAL_VALUE);
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 매장 선택
   * --
   * @param {number} idx
   */
  const handleSelect = (idx) => {
    const isSame = currentShop?.shop_information_id === idx;

    if (isSame) {
      setCurrentShop(null);
      setInputValue(INITIAL_VALUE);
      setDefaultValue(INITIAL_VALUE);
    } else {
      const { store_information = null } =
        StoreList.find((store) => store?.shop_information_id === idx) ?? {};

      if (!store_information) {
        setCurrentShop(null);
        setInputValue(INITIAL_VALUE);
        setDefaultValue(INITIAL_VALUE);
      }

      // 입력할 값만 추출
      const inputData = Object.entries(INITIAL_VALUE).reduce(
        (acc, [key, value]) => {
          const isExist = store_information[key] ?? null;
          if (isExist) {
            acc[key] = isExist;
          } else {
            acc[key] = value;
          }

          return acc;
        },
        {}
      );

      setCurrentShop(store_information);
      setInputValue(inputData);
      setDefaultValue(inputData);
    }
  };
  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string | object} v
   */
  const handleChange = (t, v) => {
    if (t === 'all') {
      setInputValue(v);
    } else {
      setInputValue((prev) => ({
        ...prev,
        [t]: v,
      }));
    }
  };

  /**
   * 영수증 정보 수정
   * --
   */
  const handleSubmit = async (idx) => {
    if (!idx) return;

    const confirm = window.confirm('해당 정보를 저장하시겠습니까?');

    if (!confirm) return;

    try {
      setIsLoading(true);

      const updateData = {
        shop_information_id: idx,
        store_information: {
          ...currentShop,
          ...inputValue,
        },
      };

      const { status, data } = await updateShopInformation(updateData);

      if (status !== 200) {
        MessageAlert.info('저장하는데 실패하였습니다!');
        return;
      }

      const queryKey = shopInformationQueryKeys.getShopInformations(query);

      // 캐싱된 데이터 업데이트
      queryClient.setQueryData(queryKey.queryKey, (prev) => {
        const { data: oldData, ...rest } = prev;

        const newData = oldData?.map((store) =>
          store?.shop_information_id === idx ? { ...store, ...data } : store
        );

        return {
          ...rest,
          data: newData,
        };
      });
      MessageAlert.success('입력하신 정보가 성공적으로 저장되었습니다.');
    } catch (err) {
      console.error('[handleSubmit] Error !! ', err);
      MessageAlert.error('저장하는데 오류가 발생하였습니다!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 매장 데이터 정제
   * --
   */
  const StoreList = React.useMemo(() => {
    if (!Stores.length) return [];

    return Stores?.filter((item) => item?.application_status !== 'REJECT')?.map(
      (_item) => ({
        key: _item?.shop_information_id,
        ..._item,
      })
    );
  }, [Stores]);

  /**
   * 초기값 설정
   * --
   */
  React.useEffect(() => {
    if (StoreList.length) {
      handleSelect(
        defaultShopId ? defaultShopId : StoreList?.[0]?.shop_information_id
      );
    }
  }, [defaultShopId, StoreList]);

  /* ===== RENDER ===== */
  return (
    <>
      <StoreReceiptPresenter
        stores={StoreList}
        currentShop={currentShop}
        inputValue={inputValue}
        defaultValue={defaultValue}
        isLoading={isLoading}
        onSelect={handleSelect}
        onChange={handleChange}
        onSubmit={handleSubmit}
      />
    </>
  );
};

StoreReceiptContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default StoreReceiptContainer;
